import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "../../ui/modal";
import Alert from "../../ui/alert/Alert";
import Toast from "../../ui/toast/Toast";
import EnterPinModal from "../../ui/modal/EnterPinModal";
import { ChevronDownIcon } from "../../../icons";
import { AlertCircle } from "lucide-react";
import { CryptoWallet, CryptoNetwork, CryptoToken, getNetworkInfo } from "../types";
import { NetworkIcon, TokenIcon } from "../CryptoIcons";
import { withdrawCrypto, fetchCryptoWithdrawalFee } from "../API/services";
import { cache } from "../../../utils";
import { User as UserType } from "../../../stores/useUserStore";
import axios from "axios";
import CreatePinModalNew from "../../ui/modal/CreatePinModalNew";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  wallet: CryptoWallet | null;
  token: CryptoToken;
  onSuccess: () => void;
}

const EVM_ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/;
const TRON_ADDRESS_PATTERN = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
const SOLANA_ADDRESS_PATTERN = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

const EVM_NETWORKS: CryptoNetwork[] = ["ethereum", "polygon", "arbitrum", "base"] as CryptoNetwork[];
const TRON_NETWORKS: CryptoNetwork[] = ["tron"] as CryptoNetwork[];
const SOLANA_NETWORKS: CryptoNetwork[] = ["solana"] as CryptoNetwork[];

function getAddressPattern(network: CryptoNetwork | null): RegExp | null {
  if (!network) return null;
  if (EVM_NETWORKS.includes(network)) return EVM_ADDRESS_PATTERN;
  if (TRON_NETWORKS.includes(network)) return TRON_ADDRESS_PATTERN;
  if (SOLANA_NETWORKS.includes(network)) return SOLANA_ADDRESS_PATTERN;
  return null;
}

const WithdrawCryptoModal = ({ isOpen, onClose, wallet, token, onSuccess }: Props) => {
  const [step, setStep] = useState<"form" | "summary">("form");
  const [showPinModal, setShowPinModal] = useState(false);
  const [showCreatePinModal, setShowCreatePinModal] = useState(false);
  const justCreatedPin = useRef(false);

  // network — now just the CryptoNetwork value itself, metadata comes from getNetworkInfo()
  const [selectedNetwork, setSelectedNetwork] = useState<CryptoNetwork | null>(null);
  const [showNetworkDropdown, setShowNetworkDropdown] = useState(false);

  // form fields
  const [address, setAddress] = useState("");
  const [memo, setMemo] = useState("");
  const [amount, setAmount] = useState("");
  const [displayAmount, setDisplayAmount] = useState("");
  const [narration, setNarration] = useState("");

    const parsedAmount = parseFloat(amount) || 0;

  // fee
  const [fee, setFee] = useState(0);
  const [loadingFee, setLoadingFee] = useState(false);

  // general
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [minPayout, setMinPayout] = useState<number | null>(null);

  // Networks that actually have an available balance entry for this token
  const availableNetworks = useMemo(() => {
    if (!wallet) return [];
    return wallet.balances
      .filter((b) => b.token === token && b.available !== false)
      .map((b) => b.network);
  }, [wallet, token]);

  const hasMultipleNetworks = availableNetworks.length > 1;

  const selectedNetworkInfo = selectedNetwork ? getNetworkInfo(selectedNetwork) : null;
  // ASSUMPTION: getNetworkInfo() return type includes `requiresMemo`. If it's named
  // differently (e.g. `memoRequired`), update this line to match.
  const requiresMemo = !!(selectedNetworkInfo as any)?.requiresMemo;

  // Balance available on the selected network for this token (not the wallet-wide total,
  // since withdrawals are network-scoped)
  const networkBalance = useMemo(() => {
    if (!wallet || !selectedNetwork) return 0;
    return (
      wallet.balances.find((b) => b.network === selectedNetwork && b.token === token)?.balance ?? 0
    );
  }, [wallet, selectedNetwork, token]);


 const addressPattern = useMemo(() => getAddressPattern(selectedNetwork), [selectedNetwork]);

  const ownAddressForNetwork = useMemo(() => {
    if (!selectedNetwork || !wallet) return null;
    if (EVM_NETWORKS.includes(selectedNetwork)) return (wallet as any).address ?? null;
    if (TRON_NETWORKS.includes(selectedNetwork)) return (wallet as any).tronAddress ?? null;
    if (SOLANA_NETWORKS.includes(selectedNetwork)) return (wallet as any).solanaAddress ?? null;
    return null;
  }, [wallet, selectedNetwork]);

  const addressError = useMemo(() => {
    const trimmed = address.trim();
    if (!trimmed || !selectedNetwork) return null;
    if (ownAddressForNetwork && trimmed.toLowerCase() === ownAddressForNetwork.toLowerCase()) {
      return "You can't withdraw to your own wallet address.";
    }
    if (addressPattern && !addressPattern.test(trimmed)) {
      return `Enter a valid ${selectedNetworkInfo?.name ?? selectedNetwork} address.`;
    }
    return null;
  }, [address, selectedNetwork, addressPattern, ownAddressForNetwork, selectedNetworkInfo]);
  
  /* ---------------- Default network on open ---------------- */
  useEffect(() => {
    if (isOpen && availableNetworks.length) {
      setSelectedNetwork(availableNetworks[0]);
    } else if (isOpen) {
      setSelectedNetwork(null);
    }
  }, [isOpen, availableNetworks]);

useEffect(() => {
  const loadFee = async () => {
    if (!selectedNetwork || parsedAmount <= 0) {
      setFee(0);
      setMinPayout(null);
      return;
    }
    setLoadingFee(true);
    try {
      const res: any = await fetchCryptoWithdrawalFee(token, parsedAmount.toString());

      // Handles either { fee, minPayout } directly, or { data: { fee, minPayout } }
      const raw = res?.data ?? res;
      const feeValue = parseFloat(raw?.fee ?? res) || 0;
      const minPayoutValue = raw?.minPayout != null ? parseFloat(raw.minPayout) : null;

      setFee(feeValue);
      setMinPayout(minPayoutValue);
    } finally {
      setLoadingFee(false);
    }
  };
  if (isOpen) loadFee();
}, [isOpen, selectedNetwork, token, parsedAmount]);

/* ---------------- Reset on close ---------------- */
  useEffect(() => {
    if (!isOpen) {
      setStep("form");
      setSelectedNetwork(null);
      setShowNetworkDropdown(false);
      setAddress("");
      setMemo("");
      setAmount("");
      setDisplayAmount("");
      setNarration("");
      setFee(0);
      setMinPayout(null);
      setError(null);
      setSuccess(null);
      setShowPinModal(false);
      setShowCreatePinModal(false);
      justCreatedPin.current = false;
    }
  }, [isOpen]);

  const formatWithCommas = (val: string) => {
    const clean = val.replace(/[^\d.]/g, "");
    const parts = clean.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.length > 1 ? `${parts[0]}.${parts[1]}` : parts[0];
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^\d.]/g, "");
    setAmount(raw);
    setDisplayAmount(formatWithCommas(raw));
  };

  const amountError = useMemo(() => {
  if (parsedAmount <= 0) return null;

  if (minPayout != null && parsedAmount < minPayout) {
    return `Minimum withdrawal is ${minPayout.toLocaleString(undefined, { maximumFractionDigits: 8 })} ${token}`;
  }

  if (minPayout == null && fee > parsedAmount) {
    return "Fee exceeds the amount entered.";
  }

  return null;
}, [parsedAmount, minPayout, fee, token]);

const canProceed =
  !!selectedNetwork &&
  address.trim().length > 0 &&
  !addressError &&
  parsedAmount > 0 &&
  parsedAmount + fee <= networkBalance &&
  !amountError &&
  !(requiresMemo && !memo.trim());

  const handleContinue = () => {
    if (!canProceed) return;
    setStep("summary");
  };

const handleConfirm = () => {
  const user = cache.get("USER") as UserType | null;

  if (user && user.hasPin === false) {
    setShowCreatePinModal(true);
  } else {
    setShowPinModal(true);
  }
};

  const handlePinVerified = async (pin: string): Promise<boolean> => {
    if (!wallet || !selectedNetwork) return false;
    setLoading(true);
    setError(null);
    try {
      const user = cache.get("USER") as UserType | null;
      const token_ = user?.token;
      let validationResponse;
      try {
        validationResponse = await axios.post(
          `${import.meta.env.VITE_BASE_URL}/user/pin/validate`,
          { pin },
          { headers: { Authorization: `Bearer ${token_}` } }
        );
      } catch {
        return false;
      }
      if (!validationResponse?.data?.success) {
        return false;
      }

      try {
        await withdrawCrypto({
          network: selectedNetwork,
          token,
          toAddress: address.trim(),
          amount: parsedAmount.toString(),
          memo: memo.trim() || undefined,
          narration: narration || undefined,
        });
      } catch (err: any) {
        console.log("WITHDRAW ERROR:", err);
        setShowPinModal(false);
        setStep("form");
        setError(err?.response?.data?.message || "Withdrawal failed. Please try again.");
        return true; // tells EnterPinModal the PIN itself was fine
      }

      setShowPinModal(false);
      setSuccess("Withdrawal submitted! Your wallet has been debited.");
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 3000);

      return true;
    } finally {
      setLoading(false);
    }
  };


  const handlePinCreated = async (pin: string): Promise<boolean> => {
  try {
    const user = cache.get("USER") as UserType | null;
    const token_ = user?.token;

    await axios.post(
      `${import.meta.env.VITE_BASE_URL}/user/pin`,
      { pin, confirmPin:pin },
      { headers: { Authorization: `Bearer ${token_}` } }
    );

    if (user) cache.set("USER", { ...user, hasPin: true }); 

    return true;
  } catch (err) {
    console.log("CREATE PIN ERROR:", err);
    return false;
  }
};

  if (!wallet) return null;

  return (
    <>
      <Modal
        // isOpen={isOpen && !showPinModal}
        isOpen={isOpen && !showPinModal && !showCreatePinModal}
        onClose={onClose}
        className="max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900"
      >
        {success && (
          <div className="mb-4">
            <Toast variant="success" title={success} message="Your wallet has been debited" />
          </div>
        )}

        {/* ── FORM STEP ───────────────────────────────────────────────────── */}
        {step === "form" && (
          <>
            <div className="mb-1 flex items-center gap-2">
              <TokenIcon token={token} size={20} />
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Withdraw {token}
              </h2>
            </div>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
              {selectedNetwork
                ? `Available on ${selectedNetworkInfo?.name ?? selectedNetwork}: `
                : "Available: "}
              {networkBalance.toLocaleString(undefined, { maximumFractionDigits: 8 })} {token}
            </p>

            {error && (
              <div className="mb-4">
                <Alert variant="error" title="Error" message={error} />
              </div>
            )}

            {availableNetworks.length === 0 && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-400">
                No {token} balance is available to withdraw on any network.
              </div>
            )}

            {/* Network */}
            {hasMultipleNetworks && (
              <div className="mb-5">
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Network
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowNetworkDropdown((p) => !p)}
                    className="flex w-full items-center gap-2 rounded-lg border border-gray-200
                      bg-gray-50 px-4 py-3 text-gray-800
                      hover:bg-gray-100 focus:outline-none
                      dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
                  >
                    {selectedNetwork && <NetworkIcon network={selectedNetwork} size={18} />}
                    <span>{selectedNetworkInfo?.name ?? "Choose network"}</span>
                    <ChevronDownIcon className="ml-auto h-5 w-5 text-gray-400" />
                  </button>

                  {showNetworkDropdown && (
                    <div
                      className="absolute z-20 mt-1 max-h-60 w-full overflow-auto
                      rounded-md border border-gray-200 bg-white shadow-lg
                      dark:border-gray-700 dark:bg-gray-800"
                    >
                      {availableNetworks.map((network) => (
                        <div
                          key={network}
                          onClick={() => {
                            setSelectedNetwork(network);
                            setShowNetworkDropdown(false);
                          }}
                          className="flex cursor-pointer items-center gap-2 px-4 py-2
                            hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <NetworkIcon network={network} size={18} />
                          <span className="text-gray-700 dark:text-white">
                            {getNetworkInfo(network).name}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Destination address */}
            <div className="mb-5">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Wallet Address
              </label>
              {/* <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value.trim())}
                placeholder="Enter destination wallet address"
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 font-mono text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              /> */}

              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value.trim())}
                placeholder="Enter destination wallet address"
                className={`w-full rounded-lg border bg-gray-50 px-4 py-3 font-mono text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30 dark:bg-gray-800 dark:text-white ${
                  addressError
                    ? "border-red-300 focus:ring-red-200 dark:border-red-800"
                    : "border-gray-200 dark:border-gray-700"
                }`}
              />
              {addressError && (
                <div className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {addressError}
                </div>
              )}
            </div>

            {/* Memo, when the selected network requires one */}
            {requiresMemo && (
              <div className="mb-5">
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Memo / Tag
                </label>
                <input
                  type="text"
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  placeholder="Required for this network"
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </div>
            )}

            {/* Amount */}
            <div className="mb-5">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Amount ({token})
              </label>
              <input
                type="text"
                value={displayAmount}
                onChange={handleAmountChange}
                placeholder="0.00"
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              />
{parsedAmount + fee > networkBalance && parsedAmount > 0 && (
  <div className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
    <AlertCircle className="h-4 w-4 flex-shrink-0" />
    Insufficient balance to cover amount + fee.
  </div>
)}

{amountError && (
  <div className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
    <AlertCircle className="h-4 w-4 flex-shrink-0" />
    {amountError}
  </div>
)}

<div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
  {loadingFee ? (
    <span>Calculating fee…</span>
  ) : parsedAmount > 0 ? (
    <span>Fee: {fee.toLocaleString(undefined, { maximumFractionDigits: 8 })} {token}</span>
  ) : null}
</div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleContinue}
                disabled={!canProceed}
                className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Continue
              </button>
            </div>
          </>
        )}

        {/* ── SUMMARY STEP ────────────────────────────────────────────────── */}
        {step === "summary" && selectedNetwork && (
          <>
            <h2 className="mb-6 text-lg font-semibold text-gray-800 dark:text-white/90">
              Confirm Withdrawal
            </h2>

            <div className="mb-6 space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Token</span>
                <span className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                  <TokenIcon token={token} size={16} />
                  {token}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Network</span>
                <span className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                  <NetworkIcon network={selectedNetwork} size={16} />
                  {selectedNetworkInfo?.name ?? selectedNetwork}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">To</span>
                <span className="max-w-[65%] break-all text-right font-mono text-sm text-gray-700 dark:text-gray-300">
                  {address}
                </span>
              </div>
              {memo && (
                <div className="flex justify-between gap-4">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Memo/Tag</span>
                  <span className="max-w-[65%] break-all text-right font-mono text-sm text-gray-700 dark:text-gray-300">
                    {memo}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Amount</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {parsedAmount.toLocaleString(undefined, { maximumFractionDigits: 8 })} {token}
                </span>
              </div>
              {fee > 0 && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Fee</span>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {fee} {token}
                  </span>
                </div>
              )}
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Total</span>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                      {(fee + parsedAmount).toLocaleString(undefined, { maximumFractionDigits: 8 })} {token}
                  </span>
                </div>
              {narration && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Narration</span>
                  <span className="max-w-[60%] text-right text-sm text-gray-700 dark:text-gray-300">
                    {narration}
                  </span>
                </div>
              )}
            </div>

            <div className="mb-6 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-400">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <p className="text-xs">
                Crypto withdrawals cannot be reversed. Double check the address and network
                before confirming.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setStep("form")}
                className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Back
              </button>
              <button
                onClick={handleConfirm}
                className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Enter PIN
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* PIN Modal */}
      <EnterPinModal
        isOpen={showPinModal}
        onClose={() => setShowPinModal(false)}
        onPinVerified={handlePinVerified}
        title="Confirm Withdrawal"
        description="Enter your 4-digit PIN to complete this withdrawal"
        showForgotPin={true}
      />

          {/* Shown only if the user has no PIN yet (AUTHORISATION_ERROR) */}
          <CreatePinModalNew
  isOpen={showCreatePinModal}
  onClose={() => {
    setShowCreatePinModal(false);
    if (justCreatedPin.current) {
      justCreatedPin.current = false;
      setShowPinModal(true);
    }
  }}
  onPinCreated={async (pin) => {
    const ok = await handlePinCreated(pin);
    if (ok) justCreatedPin.current = true;
    return ok;
  }}
/>
    </>
  );
};

export default WithdrawCryptoModal;